import { useEffect, useRef, useState } from "react";
import abcjs, { type NoteTimingEvent } from "abcjs";
import { trimXHeadStems } from "./PlaybackNotation";

interface LearnStep {
  time: number;
  notes: Element[];
  x: number;
  y: number;
  bottom: number;
}

export interface LearnTimeline {
  positions: number[];
  duration: number;
}

/** abcjs groups simultaneous voices at one time. Rests are not learning steps. */
function learningSteps(events: NoteTimingEvent[]): LearnStep[] {
  const steps = new Map<number, LearnStep>();
  for (const event of events) {
    if (event.type !== "event" || !Number.isFinite(event.milliseconds)) continue;
    const notes = new Set<Element>();
    for (const element of event.elements?.flat() ?? []) {
      const note = element.closest(".abcjs-note");
      if (note) notes.add(note);
    }
    if (notes.size === 0) continue;
    const heads = [...notes].flatMap(note => [...note.querySelectorAll<SVGGraphicsElement>(".abcjs-notehead")]);
    if (heads.length === 0) continue;
    const boxes = heads.map(head => head.getBBox());
    const noteBoxes = [...notes].map(note => (note as SVGGraphicsElement).getBBox());
    const previous = steps.get(event.milliseconds);
    steps.set(event.milliseconds, {
      time: event.milliseconds,
      notes: [...new Set([...(previous?.notes ?? []), ...notes])],
      // Grace notes belong to their main hit; the rightmost head is that hit.
      x: Math.max(...boxes.map(box => box.x + box.width / 2)),
      y: Math.max(36, Math.min(...noteBoxes.map(box => box.y), event.top ?? Infinity) - 26),
      bottom: Math.max(...noteBoxes.map(box => box.y + box.height)),
    });
  }
  return [...steps.values()].sort((a, b) => a.time - b.time);
}

/** Manual, written-note navigation, independent of the audio playback clock. */
export function LearnNotation({ abc, step, onStepCount, onTimeline }: {
  abc: string;
  step: number;
  onStepCount: (count: number) => void;
  onTimeline?: (timeline: LearnTimeline) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const steps = useRef<LearnStep[]>([]);
  const marker = useRef<SVGGElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    steps.current = [];
    marker.current = null;
    try {
      // The dialog already names the groove. Keep the score's space for the
      // stave and indicator rather than a second title and playback tempo.
      const score = abc.replace(/^T:.*(?:\r?\n|$)/gm, "")
        .replace(/^%%text ♩ = .*?(?:\r?\n|$)/gm, "")
        .replace(/^K:/m, "%%staffsep 90\n%%sysstaffsep 90\nK:");
      const tune = abcjs.renderAbc(container, score, {
        add_classes: true,
        responsive: "resize",
        staffwidth: 740,
        paddingtop: 56,
        paddingbottom: 24,
      })[0];
      const svg = container.querySelector("svg");
      // Keep abcjs's scalable viewBox, while CSS owns the surrounding layout.
      // Its percentage-padding container would clip a wide score on a phone.
      container.removeAttribute("style");
      svg?.removeAttribute("style");
      const width = Number(svg?.getAttribute("viewBox")?.split(/\s+/)[2]);
      if (Number.isFinite(width) && width > 760) container.style.minWidth = `${width}px`;
      trimXHeadStems(container);
      // abcjs returns a timeline here, although its declaration says void.
      // Real-renderer tests pin this boundary. It combines hands and feet by
      // onset, including triplets, rather than counting SVG heads.
      const events: unknown = tune?.setTiming(60);
      steps.current = Array.isArray(events) ? learningSteps(events) : [];
      onTimeline?.({ positions: steps.current.map(item => item.time),
        duration: Array.isArray(events) ? events.at(-1)?.milliseconds ?? 0 : 0 });
      if (svg && steps.current.length > 0) {
        const ns = "http://www.w3.org/2000/svg";
        const group = document.createElementNS(ns, "g");
        group.setAttribute("class", "groove-learn-marker");
        group.setAttribute("aria-hidden", "true");
        // Include the notes below the arrow in the scrolling target, so moving
        // onto a new stave keeps both the indicator and the drums in view.
        const focusArea = document.createElementNS(ns, "rect");
        focusArea.setAttribute("class", "groove-learn-focus-area");
        focusArea.setAttribute("x", "-20");
        focusArea.setAttribute("y", "-34");
        focusArea.setAttribute("width", "40");
        focusArea.setAttribute("fill", "transparent");
        focusArea.setAttribute("stroke", "none");
        const arrow = document.createElementNS(ns, "g");
        arrow.setAttribute("class", "groove-learn-arrow");
        const shape = document.createElementNS(ns, "path");
        shape.setAttribute("d", "M -5 -21 H 5 V -3 H 13 L 0 13 L -13 -3 H -5 Z");
        shape.setAttribute("fill", "#f59e0b");
        shape.setAttribute("stroke", "#172554");
        shape.setAttribute("stroke-width", "3");
        shape.setAttribute("stroke-linejoin", "round");
        arrow.append(shape);
        group.append(focusArea, arrow);
        svg.append(group);
        marker.current = group;
      }
      setFailed(false);
      onStepCount(steps.current.length);
    } catch {
      container.replaceChildren();
      steps.current = [];
      setFailed(true);
      onStepCount(0);
      onTimeline?.({ positions: [], duration: 0 });
    }
  }, [abc, onStepCount, onTimeline]);

  useEffect(() => {
    for (const note of ref.current?.querySelectorAll(".groove-learn-current") ?? []) {
      note.classList.remove("groove-learn-current");
    }
    const current = steps.current[step];
    if (!current || !marker.current) return;
    for (const note of current.notes) note.classList.add("groove-learn-current");
    marker.current.setAttribute("transform", `translate(${current.x} ${current.y})`);
    marker.current.setAttribute("data-step", String(step));
    marker.current.querySelector("rect")?.setAttribute("height", String(current.bottom - current.y + 48));
    const follow = () => marker.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
    follow();
    const viewport = ref.current?.parentElement;
    if (!viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(follow);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [abc, step]);

  return <div className="groove-learn-notation" ref={ref}>
    {failed && <p role="status">This score could not be drawn. Close Learn mode to keep editing your groove.</p>}
  </div>;
}
